import { Route as RootRoute } from "@/routes/__root";
import { router } from "@/App";
import { imageAssetUrl } from "@/utils/url";
import { mfCentralRefetchInterval } from "creso-core/constants";
import {
  useMandates,
  useMfCentralImportState,
  useMfCentralStatus,
  usePortfolioCategorySummary,
  usePortfolioSummaryV2,
} from "creso-core/hooks";
import { Client } from "creso-core/types";
import { CheckCircle2Icon } from "lucide-react";
import { useMemo } from "react";
import {
  importEmptyImg,
  NoDataAnimation,
  SyncingLottieAnimation,
  TimeoutLottieAnimation,
} from "web-core";
import { Button, cn } from "web-design-system";
import { useSearch } from "@tanstack/react-router";

export function WhatNext({
  client,
  hasPlacedTransaction,
  isLoadingOrders,
}: {
  client: Client;
  hasPlacedTransaction: boolean;
  isLoadingOrders: boolean;
}) {
  const cardClassName = cn(
    "flex-1 h-90 relative bg-card flex rounded-lg items-center justify-center flex-col border border-border px-4"
  );

  const { mandates, isLoading: isLoadingMandates } = useMandates(
    client.investment_account ?? ""
  );

  const { data: importStatus, isLoading: isLoadingStatus } = useMfCentralStatus(
    client.investment_account || "",
    !!client.investment_account,
    mfCentralRefetchInterval
  );

  const importState = useMfCentralImportState(importStatus);

  const { data: summary, isLoading: isLoadingSummary } = usePortfolioSummaryV2(
    client.investment_account || "",
    importStatus?.status === "SUCCESS" && !!client?.investment_account
  );

  usePortfolioCategorySummary(
    { investmentAccountId: client.investment_account ?? undefined },
    {
      enabled:
        importStatus?.status === "SUCCESS" && !!client?.investment_account,
    }
  );

  const { modal } = useSearch({ from: RootRoute.id });

  const isMfCentralModalOpen = modal === "import-holdings";

  const ITEMS = useMemo(() => {
    const externalSummary = summary?.find((s) => s.source_type === "EXTERNAL");

    const zeroValueExternalHolding =
      importStatus?.last_fetch_at &&
      externalSummary &&
      Number(externalSummary.invested_value) === 0;

    return [
      {
        label: "Setup Mandate",
        description: "Critical, get it done at the earliest. Needed for SIPs ",
        completed: !!mandates?.length,
        action: {
          label: "Create Mandate",
          onClick: (client: Client) =>
            router.navigate({
              to: ".",
              search: {
                modal: "mandate",
                client_id: client.id,
                context: "create-form",
              },
            }),
        },
        img: (
          <img
            src={imageAssetUrl("/portfolio/mandate-empty.png")}
            className="h-full aspect-auto"
          />
        ),
      },
      {
        label:
          importState === "in-progress"
            ? "Importing Holdings"
            : importState === "expired"
              ? "Taking longer than usual"
              : zeroValueExternalHolding
                ? "No data found"
                : "Import External Holdings",
        description:
          importState === "in-progress"
            ? "External portfolio should appear on the platform in a few mins."
            : importState === "expired"
              ? "Holdings are still being imported, might take up to 10-15 mins, come back later."
              : zeroValueExternalHolding
                ? "No mutual fund holdings found."
                : "Assess client portfolio with Semantic Asset Allocation",
        completed: !!importStatus?.last_fetch_at,
        action: {
          label: "Open MF Central",
          onClick: (client: Client) =>
            router.navigate({
              to: ".",
              search: {
                modal: "import-holdings",
                client_id: client.id,
              },
            }),
        },
        img:
          importState === "in-progress" ? (
            isMfCentralModalOpen ? (
              <img
                src={importEmptyImg}
                className="h-full aspect-auto scale-90"
              />
            ) : (
              <SyncingLottieAnimation height={160} width={160} />
            )
          ) : importState === "expired" ? (
            <TimeoutLottieAnimation loop={false} height={160} width={160} />
          ) : zeroValueExternalHolding ? (
            <NoDataAnimation loop={false} height={160} width={160} />
          ) : (
            <img src={importEmptyImg} className="h-full aspect-auto scale-90" />
          ),
      },
      {
        label: "First Transaction",
        description: "Cement the relationship, start investing",
        completed: hasPlacedTransaction,
        action: {
          label: "Start Transacting",
          onClick: (client: Client) =>
            router.navigate({
              to: ".",
              search: {
                modal: "transaction",
                client_id: client.id,
              },
            }),
        },
        img: (
          <img
            src={imageAssetUrl("/portfolio/transact-empty.png")}
            className="h-full aspect-auto"
          />
        ),
      },
    ];
  }, [
    importStatus,
    importState,
    mandates,
    summary,
    hasPlacedTransaction,
    isMfCentralModalOpen,
  ]);

  const isLoading =
    isLoadingMandates || isLoadingStatus || isLoadingSummary || isLoadingOrders;

  if (isLoading) return null;

  return (
    <div className="flex flex-col flex-1 gap-6">
      <span className="text-base font-semibold">What to do next?</span>
      <div className="flex items-center gap-4">
        {ITEMS.map((i) => (
          <div
            key={i.label}
            className={cn(cardClassName, i.completed && "bg-secondary")}
          >
            <div className="h-40 w-60 flex justify-center">{i.img}</div>
            <span className="font-semibold mt-8">{i.label}</span>
            <span className="text-muted-foreground mt-2 text-sm text-center">
              {i.description}
            </span>
            <Button
              className="mt-4"
              variant="outline"
              disabled={i.completed}
              onClick={() => i.action.onClick(client)}
            >
              <CheckCircle2Icon
                className={cn(
                  "text-constructive hidden",
                  i.completed && "flex"
                )}
              />
              {i.completed ? "Complete" : i.action.label}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
